import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { TrashIcon} from '@heroicons/react/24/outline'
import i18n from '../../i18n'
import { apiDel } from '../../libs/apiHelper'


export default function DeleteModal({open, setOpen, template, setTemplates}) {

  const cancelButtonRef = useRef(null)

  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) setError(null)
  }, [open, template])

  // apiDel usa el token de impersonación si lo hay (antes se usaba siempre el del admin y el
  // borrado iba a su propia cuenta). La tarjeta solo se quita si el backend confirma el borrado.
  const deleteTemplate = async () => {
    if (deleting) return
    setDeleting(true)
    setError(null)
    try {
      await apiDel('/campaign-type', { body: { name: template.name } })
      setTemplates(oldTemplates => oldTemplates.filter(t => t.name !== template.name))
      setOpen(false)
    } catch (err) {
      const status = err?.response?.status
      const data = err?.response?.data || {}
      if (status === 409) {
        setError(i18n.t('template_has_campaigns', { count: data.campaigns || 0 }))
      } else if (status === 404) {
        setError(i18n.t('template_not_found'))
      } else {
        setError(data.error || i18n.t('template_delete_error'))
      }
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <TrashIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      {i18n.t('delete_template')}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {i18n.t('are_you_sure_delete',{name : template ? template.name : ""})}
                      </p>
                      {error && (
                        <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                          {error}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => deleteTemplate()}
                    disabled={deleting || !!error}
                  >
                    {deleting ? i18n.t('deleting') : i18n.t('delete')}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    onClick={() => setOpen(false)}
                    ref={cancelButtonRef}
                  >
                    {i18n.t('cancel')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}